import { Component, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { AuthService } from '../../../core/services/auth.service';
import { ROLE_LABELS, Role } from '../../../core/enums/role.enum';
import { SidebarComponent } from '../sidebar/sidebar.component';

@Component({
  selector: 'app-topbar',
  standalone: true,
  imports: [CommonModule],
  template: `
    <header class="fixed top-0 left-0 right-0 h-20 bg-white border-b border-gray-200/80 flex items-center z-50">

      <!-- Brand lockup — matches sidebar width on desktop, auto on mobile -->
      <div
        class="flex items-center gap-3 px-4 h-full shrink-0 transition-all duration-300"
        [ngClass]="{
          'lg:w-64 lg:px-6': !sidebarRef?.collapsed(),
          'lg:w-20 lg:justify-center lg:px-0': sidebarRef?.collapsed()
        }"
      >
        <!-- Mobile Hamburger -->
        <button
          class="lg:hidden text-gray-500 hover:text-gray-900 focus:outline-none p-1 -ml-1 transition-colors cursor-pointer"
          (click)="onMenuToggle()"
          aria-label="Toggle navigation"
        >
          <svg class="w-6 h-6" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" d="M4 6h16M4 12h16M4 18h16" />
          </svg>
        </button>

        <!-- Transgenie egg mark -->
        <img
          src="transgenie-egg.png"
          alt="Transgenie"
          class="shrink-0 object-contain h-12 w-auto transition-all duration-300"
        />

        <!-- Wordmark — desktop, expanded only -->
        @if (!sidebarRef?.collapsed()) {
          <span class="hidden lg:block text-lg font-extrabold tracking-[0.08em] whitespace-nowrap text-brand-charcoal leading-none">
            TRANSGENIE
          </span>
        }
      </div>

      <!-- Page title -->
      <div class="flex-1 min-w-0 px-4 sm:px-6 border-l border-gray-100 h-full flex items-center">
        <h1 class="text-base lg:text-lg font-semibold text-gray-900 tracking-tight truncate">
          {{ getRoleLabel() }} Portal
        </h1>
      </div>

      <!-- User + Logout -->
      <div class="flex items-center gap-2 sm:gap-3 px-4 sm:px-6 shrink-0">
        <div class="flex items-center gap-2.5">
          <div class="w-9 h-9 rounded-full bg-primary-100 text-primary-800 flex items-center justify-center text-xs font-bold tracking-wider shrink-0">
            {{ getUserInitials() }}
          </div>
          <div class="hidden sm:flex flex-col">
            <span class="text-sm font-semibold text-gray-900 leading-tight">{{ auth.user()?.userId }}</span>
            <span class="text-[11px] font-medium text-gray-500 leading-tight">{{ getRoleLabel() }}</span>
          </div>
        </div>

        <div class="w-px h-8 bg-gray-200 hidden sm:block"></div>

        <button
          class="flex items-center justify-center w-9 h-9 rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-red-100 shrink-0 cursor-pointer"
          (click)="auth.logout()"
          title="Logout"
          aria-label="Logout"
        >
          <svg class="w-5 h-5" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" d="M15.75 9V5.25A2.25 2.25 0 0 0 13.5 3h-6a2.25 2.25 0 0 0-2.25 2.25v13.5A2.25 2.25 0 0 0 7.5 21h6a2.25 2.25 0 0 0 2.25-2.25V15m3 0 3-3m0 0-3-3m3 3H9" />
          </svg>
        </button>
      </div>
    </header>
  `,
})
export class TopbarComponent {
  readonly auth = inject(AuthService);
  sidebarRef: SidebarComponent | null = null;

  getRoleLabel(): string {
    const role = this.auth.userRole();
    return role ? (ROLE_LABELS[role] || role) : '';
  }

  getUserInitials(): string {
    const userId = this.auth.user()?.userId || '';
    return userId.substring(0, 2).toUpperCase();
  }

  onMenuToggle(): void {
    if (this.sidebarRef) {
      this.sidebarRef.toggle();
    }
  }
}
